<script>
    import { navigate } from "svelte-routing";
    /** USAGE EXAMPLE:
    <Breadcrumb
    items={[
        { id: 'home', text: 'Home', on_click: ... },
        { id: 'section', text: 'Section', on_click: ..., disabled: false },
        { id: 'dropdown', text: 'More', type: 'select', options: [
            { label: 'Option 1', onselect: ... },
            { label: 'Option 2', onselect: ... }
        ] }
    ]}
    on:select={e => ...}
    on:dropdownSelect={e => ...}
    />
**/
    import { active_breadcrumb } from "../app-store";

    // Props
    export let items = [];

    // Helper: handle click on breadcrumb button
    function handleClick(item) {
        if (item.disabled) return;
        active_breadcrumb.set(item.id);
        if (item.to) {
            navigate(item.to);
        }
    }

    // Helper: handle dropdown select
    function handleDropdownSelect(item, option) {
        if (typeof option.onselect === "function") {
            option.onselect(payload);
        }
    }

    // Only active breadcrumb should be clickable
    // and the rest should be disabled except for the always_enabled ones
    function isBreadcrumbDisabled(item_id, activeId, always_enabled) {
        if (item_id === activeId) return false;
        if (always_enabled) return false;
        return true;
    }
</script>

<nav class="breadcrumb-container" aria-label="breadcrumb">
    <ol class="breadcrumb">
        {#each items as item, idx}
            <li
                id={item.id}
                class="breadcrumb-item {$active_breadcrumb === item.id ? 'active' : ''} 
                {isBreadcrumbDisabled(item.id, $active_breadcrumb, item.alaways_enabled) ? 'disabled': ''}"
                aria-current={$active_breadcrumb === item.id ? "page" : undefined}
            >
                {#if item.type === "select"}
                    <!-- Dropdown Breadcrumb Item -->
                    <div class="btn-group">
                        <!-- Main button (title) -->
                        <button
                            type="button"
                            class="btn btn-breadcrumb btn-dropdown"
                            disabled={!item.isActive}
                            on:click={() => handleClick(item)}
                        >
                            <span class="btn-breadcrumb-icon"></span>
                            <span class="btn-breadcrumb-label">{item.label}</span>
                        </button>
                        <!-- Dropdown toggle -->
                        <button
                            type="button"
                            class="btn dropdown-toggle dropdown-toggle-split"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            disabled={!item.isActive}
                        >
                            <span class="visually-hidden">Toggle Dropdown</span>
                        </button>
                        <!-- Dropdown menu -->
                        <ul class="dropdown-menu">
                            {#each item.options as option}
                                <li>
                                    <button
                                        class="dropdown-item"
                                        type="button"
                                        disabled={item.disabled}
                                        on:click={() => handleDropdownSelect(item, option)}
                                    >
                                        {option.label}
                                    </button>
                                </li>
                            {/each}
                        </ul>
                    </div>
                {:else}
                    <!-- Regular Breadcrumb Button -->
                    <button
                        class="btn btn-breadcrumb"
                        id={item.id}
                        type="button"
                        disabled={item.disabled}
                        on:click={() => handleClick(item)}
                    >
                        <span class="btn-breadcrumb-icon"></span>
                        <span class="btn-breadcrumb-label">{item.label}</span>
                    </button>
                {/if}
            </li>
        {/each}
    </ol>
</nav>

<style>
    .breadcrumb-container {
        width: 100%;
    }
    .breadcrumb {
        list-style: none;
        padding-left: 1rem;
        margin-bottom: 0;
        --bs-breadcrumb-divider: ">";
    }
    .breadcrumb-item,
    .btn-breadcrumb {
        overflow: hidden;
        display: flex;
        align-items: center;
        position: relative;
    }
    /* on really small screen (0px-521px) only show the active item */
    .breadcrumb-item:not(.active) {
        display: none;
    }
    .breadcrumb-item.disabled .btn-breadcrumb {
        cursor: auto;
        opacity: 0.25;
        background-color: transparent;
    }
    .btn-breadcrumb:not(:disabled):hover,
    .dropdown-toggle:not(:disabled):hover {
        background-color: hsl(0, 0%, 80%);
        color: var(--prione);
    }
    .breadcrumb-item.active .btn-breadcrumb {
        color: #fff;
        opacity: 1;
        background-color: #7f5700;
        border-radius: 2rem;
    }
    .breadcrumb-item.disabled .btn-breadcrumb {
        box-shadow: none;
        opacity: 0.5;
    }
    .breadcrumb-item.active .btn {
        font-weight: bold;
        pointer-events: none;
    }
    .breadcrumb-item.disabled .btn {
        opacity: 0.5;
        pointer-events: none;
    }
    .breadcrumb-item:not(.active) .dropdown-toggle {
        display: none;
    }
    .btn-breadcrumb {
        background-color: #fff;
        display: grid;
        grid-template-columns: 26px auto;
    }
    .btn-breadcrumb-icon {
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        width: auto;
        height: 20px;
    }
    .breadcrumb-item:not(.active) .btn-breadcrumb-icon {
        filter: brightness(0.5);
    }
    #breadcrumb-project-list .btn-breadcrumb-icon {
        background-image: url("/img/list-icon.svg");
    }
    #breadcrumb-project .btn-breadcrumb-icon {
        background-image: url("/img/project-icon.svg");
    }
    #breadcrumb-new-draft .btn-breadcrumb-icon {
        background-image: url("/img/project-icon.svg");
    }
    #breadcrumb-recommended-sources .btn-breadcrumb-icon {
        background-image: url("/img/recommended-sources-icon.svg");
    }
    #breadcrumb-article-content .btn-breadcrumb-icon {
        background-image: url("/img/review-icon.svg");
    }
    .btn-breadcrumb {
        background-color: transparent;
        border: none;
        box-shadow: none;
        margin-top: 0;
        color: var(--text-muted-color-6);
        border-radius: 20px;
    }
    .btn-breadcrumb-label {
        display: none;
    }
    .btn-dropdown,
    .dropdown-toggle {
        border: none;
    }
    .dropdown-toggle {
        background-color: transparent;
        border: none;
        box-shadow: none;
        margin-top: 0;
        color: var(--text-muted-color-6);
    }
    @media screen and (min-width: 521px) {
        .breadcrumb-item:not(.active) {
            display: flex;
        }
    }
    @media screen and (min-width: 1024px) {
        .breadcrumb {
            display: flex;
        }
        .breadcrumb-item {
            width: auto;
        }
        .btn-breadcrumb-label {
            display: block;
        }
    }
</style>
